/**
 * @typedef {Object} SelectorOption
 * @property {string} label - Display label for the option
 * @property {string} value - Value identifier for the option
 */

/**
 * @typedef {Object} EnabledColumn
 * @property {string} label - Display label for the column
 * @property {string} name - Column name identifier
 */

/**
 * @typedef {Object} DatasourceTable
 * @property {EnabledColumn[]} enabledColumns - Array of enabled column definitions
 */

/**
 * @typedef {Object} SelectorData
 * @property {SelectorOption|undefined} value - Currently selected option or undefined if none selected
 * @property {SelectorOption[]} options - Available options (excluding currently selected)
 * @property {Function} onChange - Handler function that updates filter state when selection changes
 */

/**
 * Builds selector data for metric dropdowns by extracting enabled columns from datasource table.
 * Separates the currently selected metric from available options.
 *
 * @param {string} metric - The currently selected metric name (e.g., "fill_rate")
 * @param {DatasourceTable} table - Datasource table object containing enabledColumns array
 * @param {string} key - Filter query argument key for updating URL params
 * @param {Function} setFilters - Function to update filter state in URL
 * @returns {SelectorData} Selector data object with value, options, and onChange handler
 */
export const buildSelectorData = (metric, table, key, setFilters) => {
  const all = table?.enabledColumns?.map(c => ({ label: c.label, value: c.name })) ?? []
  const value = all.find(c => c.value === metric)
  const options = all.filter(c => c.value !== metric)

  return {
    value,
    options,
    onChange: newMetric => {
      if (table == null || newMetric.value === metric) {
        return
      }

      setFilters({ [key]: newMetric.value })
    },
  }
}
